import { useState, useEffect } from 'react';
import { Target, CheckCircle, Shield } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, type UserStreak } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

const DAILY_GOAL = 5;
const STORAGE_KEY = 'mw-daily-reads';

function getTodayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function getReadCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw) as { date: string; count: number };
    if (data.date !== getTodayKey()) return 0;
    return data.count;
  } catch {
    return 0;
  }
}

/** Call this when a post is viewed to increment the daily read counter */
export function incrementDailyRead(): void {
  const today = getTodayKey();
  const current = getReadCount();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: today, count: current + 1 }));
  window.dispatchEvent(new Event('daily-read-update'));
}

export function DailyGoalWidget() {
  const [reads, setReads] = useState(getReadCount);
  // T-127: one auth source, same field the header/sidebar read.
  const isLoggedIn = useAuthStore((s) => s.isAuthenticated);

  const { data: streak } = useQuery<UserStreak>({
    queryKey: ['streak'],
    queryFn: () => api.users.getStreak(),
    enabled: isLoggedIn,
  });

  useEffect(() => {
    const handler = () => setReads(getReadCount());
    window.addEventListener('daily-read-update', handler);
    return () => window.removeEventListener('daily-read-update', handler);
  }, []);

  const progress = Math.min(reads, DAILY_GOAL);
  const done = progress >= DAILY_GOAL;
  const pct = Math.round((progress / DAILY_GOAL) * 100);
  const freezeTokens = streak?.freezeTokensRemaining ?? 0;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-violet-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10">
          {done ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <Target className="h-4 w-4 text-violet-500" />
          )}
        </div>
        <h3 className="font-semibold text-text-primary text-sm">Daily Goal</h3>
      </div>

      <div className="px-4 py-3">
        <p className="text-sm text-text-secondary">
          {done
            ? 'Goal complete! Great reading today.'
            : `Read ${DAILY_GOAL} articles today`}
        </p>

        {/* Progress bar */}
        <div className="mt-2 h-2 w-full rounded-full bg-bg-tertiary overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              done
                ? 'bg-gradient-to-r from-green-500 to-emerald-400'
                : 'bg-gradient-to-r from-violet-500 to-purple-400'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-text-tertiary">
            {progress}/{DAILY_GOAL} articles read
          </p>

          {isLoggedIn && freezeTokens > 0 && (
            <div className="flex items-center gap-1" title={`${freezeTokens} streak freeze${freezeTokens > 1 ? 's' : ''} — protects your streak if you miss a day`}>
              <Shield className="h-3.5 w-3.5 text-cyan-500" />
              <span className="text-xs font-medium text-cyan-500">{freezeTokens}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
